import { now } from '../Clock.ts';
import { BaseInputDevice, Debouncer, type ButtonEvent } from './InputDevice.ts';

/**
 * A button that exists only in software.
 *
 * This is the seam that keeps the architecture honest. `SimulatedHumanStimulator`
 * does not tell the game to flap -- it presses *this*, exactly as a stimulated
 * forearm would press a real switch, and the controller then detects it through
 * the same `InputDevice` interface a real switch uses.
 *
 * Swap this for `SerialInput` and no other line of code changes.
 */
export class VirtualButtonDevice extends BaseInputDevice {
  readonly id: string;
  readonly label: string;

  private debouncer = new Debouncer(0);
  private pressCount = 0;

  constructor(id = 'virtual-button', label = 'Virtual button (simulated participant)') {
    super();
    this.id = id;
    this.label = label;
  }

  async connect(): Promise<void> {
    this.status = 'connected';
  }

  async disconnect(): Promise<void> {
    this.status = 'disconnected';
  }

  /** Close the contact. Called by a simulated actuator, never by game code. */
  press(timestamp = now(), requestId?: string, metadata?: Record<string, unknown>): void {
    if (!this.debouncer.accept(true, timestamp)) return;
    this.pressCount++;
    const e: ButtonEvent = { down: true, timestamp, source: this.id, requestId, metadata };
    this.emitButton(e);
  }

  /** Open the contact. */
  release(timestamp = now(), requestId?: string): void {
    if (!this.debouncer.accept(false, timestamp)) return;
    this.emitButton({ down: false, timestamp, source: this.id, requestId });
  }

  /** Press and release as one movement. */
  tap(timestamp = now(), holdMs = 60, requestId?: string): void {
    this.press(timestamp, requestId);
    this.release(timestamp + holdMs, requestId);
  }

  override info(): Record<string, unknown> {
    return { ...super.info(), presses: this.pressCount };
  }
}
